import { ConstantField, MappedHeader, MappingParams } from '@shared/types/flowstep/mapping';
import { AsyncLoadable } from '@v2/utils/mobx/async-loadable';
import { autorun, reaction, runInAction, toJS } from 'mobx';
import { RangeAddress } from 'shared/excel/range-address';
import { diContainer } from 'shared/infrastructure/di-container';
import { ProviderClient, ProviderFactory } from 'shared/providers/provider';
import { DataField, DataObject } from 'shared/providers/types';
import { HSObjectType } from 'shared/services/hubspot/types';
import { Flowstep, Operation, ProviderType } from 'shared/types/flowstep';
import { RangeType } from 'shared/utils/flowstep';
import { capitalizeString } from 'shared/utils/utils';
import office from 'src/services/office';
import { BaseEditorState } from '@v2/components/flowsteps/editors/common/states/base-editor-state';
import { ValidationErrorsMap } from 'src/v2/states/validation';
import { makeAutoObservableExt } from '@v2/utils/mobx/make-auto-observable-ext';

export type PushObjectsHSErrorKey =
  | 'target-range-not-selected'
  | 'mapping-error'
  | 'unique-values-column'
  | 'column-for-id'
  | 'unique-values-field-not-selected'
  | 'object-type-not-selected';

export class PushObjectsHSState extends BaseEditorState {
  _mappedHeaders: MappedHeader[] = [];
  originalMappedHeaders: MappedHeader[] = [];
  mappedConstants: ConstantField[] = [];
  isMappingsConfirmed: boolean = true;

  copyResultIds = false;
  columnForId = '';
  onlyVisibleRows = true;
  showResultsTable = true;
  rangeToApply: { type: (typeof RangeType)[keyof typeof RangeType] } = {
    type: RangeType.Selected,
  };
  isValidRangeToApply = true;
  uniqueValuesColumn = '';

  tableHeaders: string[] = [];
  worksheetId: string = '';
  target: string = '';
  targetRangeAddress: RangeAddress | null = null;
  isDetectingRange = false;

  objectTypes = new AsyncLoadable<DataObject[]>([]);
  objectFields = new AsyncLoadable<DataField[]>([]);
  private _objectTypeName: string | undefined = '';

  private _operation: Operation;
  private providerMeta;
  private provider: ProviderClient;

  private buildMappedHeaders() {
    const result: MappedHeader[] = [];

    for (let i = 0; i < this.tableHeaders.length; i++) {
      const excelColumnName = this.tableHeaders[i];
      const existentMapping = this._mappedHeaders.find((mapping) => mapping.excelColumnName === excelColumnName);

      if (existentMapping) {
        result.push(existentMapping);
      } else {
        const providerField = findFieldForColumn(this.writableFields, excelColumnName);
        const isIdField = providerField?.name === this.providerMeta.getIdFieldName();

        result.push({
          excelColumnName,
          providerField,
          isUsed: isIdField ? this.operation !== Operation.INSERT : !!providerField,
        });

        this.isMappingsConfirmed = false;
      }
    }

    this._mappedHeaders = result;
  }

  constructor(
    operation: Operation,
    private defaultRangeToApply?: (typeof RangeType)[keyof typeof RangeType],
  ) {
    super({ operation, provider: ProviderType.HS });

    makeAutoObservableExt(this, PushObjectsHSState);

    this._operation = operation;

    const providerFactory = diContainer.resolve<ProviderFactory>('providerFactory');
    this.provider = providerFactory.getProviderClient(ProviderType.HS);
    this.providerMeta = providerFactory.getProviderMeta(ProviderType.HS);

    this.objectTypes.load(async () =>
      this.provider
        .loadObjectTypes()
        .then((objectTypes) => objectTypes.filter((objectType) => !objectType.unsupportedOps?.includes(operation))),
    );
    this.rangeToApply = { type: defaultRangeToApply || RangeType.Selected };
    this.setupReactions();
  }

  isSimilarFlowstep(flowstep: Flowstep): boolean {
    return [Operation.INSERT, Operation.UPDATE].includes(flowstep.operation) && flowstep.provider === ProviderType.HS;
  }

  tryToLoadDifferentTypeFlowstep(sameTargetFlowsteps: Flowstep[], flowstepTarget: any) {
    this.setFlowstepTarget(flowstepTarget);
    const flowstep = sameTargetFlowsteps.find((x) => x.operation === Operation.PULL && x.provider === ProviderType.HS);
    if (!flowstep) {
      return;
    }
    this._objectTypeName = flowstep.providerOptions.objectType;
  }

  applyMapping(result: MappingParams): void {
    this.isMappingsConfirmed = true;
    this.originalMappedHeaders = [];
    this._mappedHeaders = result.mappedHeaders;
    this.mappedConstants = result.constants || [];
  }

  get writableFields() {
    return this.objectFields.data.filter(
      (field) => !field.readOnly || field.name === this.providerMeta.getIdFieldName(),
    );
  }

  set objectTypeName(value: string | undefined) {
    this.uniqueValuesColumn = '';
    this._objectTypeName = value;
  }

  get objectTypeName(): string | undefined {
    return this._objectTypeName;
  }

  get validationErrors() {
    const errors = new Map();

    this.validate(errors);

    return errors;
  }

  get isMappingButtonDisabled(): boolean {
    return !this._objectTypeName || this.tableHeaders.length === 0 || !this.objectFields.isReady;
  }

  get isFlowstepButtonsDisabled(): boolean {
    return !this._objectTypeName || this.validationErrors.size > 0 || !this.isValidRangeToApply;
  }

  get selectedObjectType(): DataObject | undefined {
    const objectName = this._objectTypeName;
    return this.objectTypes.data.find((objectType) => objectType.name === objectName);
  }

  get operation() {
    return this._operation;
  }

  get generatedFlowstepName() {
    const sheetName = this.targetRangeAddress?.sheetName;
    const name =
      this.operation === Operation.INSERT
        ? `Insert into ${capitalizeString(this._objectTypeName)}`
        : `Update of ${capitalizeString(this._objectTypeName)}`;

    return name + (sheetName ? ` at '${sheetName}'` : '');
  }

  get mappedHeaders() {
    return this._mappedHeaders;
  }

  get loadingObjectTypesError() {
    return this.objectTypes.error;
  }

  get loadingObjectFieldsError() {
    return this.objectFields.error;
  }

  setupReactions() {
    autorun(() => {
      const objectType = this.selectedObjectType;
      this.objectFields.load(async () => {
        if (!objectType) return [];
        const hsType = objectType.originalType as HSObjectType;
        return await this.provider.loadObjectFields(hsType.id);
      });
    });

    reaction(
      () => ({
        objectTypeName: this._objectTypeName,
        isReady: this.objectFields.isReady,
        fields: this.objectFields.data,
        headers: this.tableHeaders,
      }),
      ({ isReady, fields, objectTypeName }, { objectTypeName: prevObjectTypeName }) => {
        if (!!prevObjectTypeName && prevObjectTypeName !== objectTypeName) {
          this._mappedHeaders = [];
        }

        if (isReady && fields.length > 0) {
          this.buildMappedHeaders();
        }
      },
    );
  }

  buildFlowstep(): Flowstep {
    return {
      id: this.flowstep?.id,
      flowId: this.flowstep?.flowId,
      operation: this._operation,
      name: this.name || this.generatedFlowstepName,
      errorReaction: this.errorReaction,
      provider: ProviderType.HS,
      version: '4',
      options: {
        ...super.buildFlowstep(),
        worksheetId: this.worksheetId,
        target: this.target,
        rangeToApply: toJS(this.rangeToApply),
        onlyVisibleRows: this.onlyVisibleRows,
        tableHeaders: toJS(this.tableHeaders),
        showOperationResultsTable: this.showResultsTable,
        mapping: {
          objectType: this._objectTypeName,
          mappedHeaders: toJS(this._mappedHeaders),
          constants: toJS(this.mappedConstants),
          columnNameForResultIds: this.copyResultIds ? this.columnForId : undefined,
          uniqueValuesColumn: this.copyResultIds ? this.uniqueValuesColumn : undefined,
        },
      },
      providerOptions: {
        objectType: this._objectTypeName,
      },
    };
  }

  async onFlowstepLoaded(flowstep: Flowstep, meta: { isExact?: boolean; flowstepTarget?: any }): Promise<void> {
    const { isExact, flowstepTarget } = meta;
    super.onFlowstepLoaded(flowstep, meta);

    this._operation = flowstep.operation;

    const {
      rangeToApply,
      tableHeaders,
      worksheetId,
      target,
      showOperationResultsTable = true,
      onlyVisibleRows = true,
    } = flowstep.options;

    this.rangeToApply = this.defaultRangeToApply ? this.rangeToApply : rangeToApply;
    this.tableHeaders = tableHeaders;
    this.worksheetId = worksheetId;
    this.target = target;
    this.showResultsTable = showOperationResultsTable;

    const {
      mappedHeaders = [],
      columnNameForResultIds,
      constants = [],
      uniqueValuesColumn,
    } = flowstep.options.mapping || {};

    this._mappedHeaders = mappedHeaders;
    this.originalMappedHeaders = mappedHeaders;
    this.mappedConstants = constants;
    this.columnForId = columnNameForResultIds;
    this.uniqueValuesColumn = uniqueValuesColumn;
    this.onlyVisibleRows = onlyVisibleRows;

    const { objectType } = flowstep.providerOptions;
    this._objectTypeName = objectType;

    this.copyResultIds = !!(this.uniqueValuesColumn && this.columnForId);

    if (flowstepTarget) {
      this.setFlowstepTarget(flowstepTarget);
    } else if (isExact) {
      await this.detectRange(true);
    }

    this.isMappingsConfirmed = !this.isNewFlowstep;
  }

  validate(errors: ValidationErrorsMap<PushObjectsHSErrorKey>): void {
    this.validateTargetRange(errors);
    this.validateObjectType(errors);
    this.validateFieldMappings(errors);
    this.validateCopyResultIds(errors);
  }

  setFlowstepTarget(flowstepTarget: any) {
    runInAction(() => {
      this.targetRangeAddress = flowstepTarget.table
        ? RangeAddress.fromString(flowstepTarget.table.rangeAddress)
        : null;
      this.worksheetId = flowstepTarget.worksheetId || '';
      this.target = flowstepTarget ? flowstepTarget.targetCell : '';
      this.tableHeaders = flowstepTarget.table?.header.values || [];
    });
  }

  async detectRange(useFlowstepTarget: boolean = false) {
    this.isDetectingRange = true;
    try {
      const result = (await office.getFlowstepTarget(
        useFlowstepTarget ? { worksheetId: this.worksheetId, targetCell: this.target } : undefined,
      )) as any;
      this.setFlowstepTarget(result);
    } finally {
      this.isDetectingRange = false;
    }
  }

  private validateTargetRange(errors: ValidationErrorsMap<PushObjectsHSErrorKey>) {
    if (!this.targetRangeAddress) {
      errors.set('target-range-not-selected', 'Target range must be selected');
    }
  }

  private validateObjectType(errors: ValidationErrorsMap<PushObjectsHSErrorKey>) {
    if (!this._objectTypeName) {
      errors.set('object-type-not-selected', 'Object type must be selected');
    }
  }

  private validateFieldMappings(errors: ValidationErrorsMap<PushObjectsHSErrorKey>) {
    if (!this.isMappingsConfirmed) {
      errors.set('mapping-error', 'Confirm mapping parameters to continue');
      return;
    }

    if (
      !this.isNewFlowstep &&
      this.tableHeaders.length !== this.originalMappedHeaders.length &&
      this.originalMappedHeaders.length
    ) {
      errors.set(
        'mapping-error',
        `Change in data structure detected. There were ${this.originalMappedHeaders.length} data columns when you created this flow step, but now there are ${this.tableHeaders.length}. Please revisit your mappings.`,
      );
    }
    if (!this._mappedHeaders.some((mappedHeaders) => mappedHeaders.isUsed)) {
      errors.set('mapping-error', 'None of mappings is enabled');
      return;
    }

    if (
      this._operation === Operation.UPDATE &&
      !this._mappedHeaders.find(
        (field) => field.isUsed && field.providerField?.name === this.providerMeta.getIdFieldName(),
      )
    ) {
      errors.set('mapping-error', 'ID field must be selected for Update operation');
    }
  }

  private validateCopyResultIds(errors: ValidationErrorsMap<PushObjectsHSErrorKey>) {
    if (this.copyResultIds) {
      if (!this.columnForId) {
        errors.set('column-for-id', 'This field is required when copying result IDs.');
      }
      if (!this.uniqueValuesColumn) {
        errors.set('unique-values-column', 'Excel column for unique values is required when copying result IDs.');
      }
    }
  }
}

const findFieldForColumn = (fields: DataField[], columnName: string) => {
  return fields.find(
    (field) =>
      field.label.toUpperCase() === columnName.toUpperCase() || field.name.toUpperCase() === columnName.toUpperCase(),
  );
};

const findField = (fields: DataField[], fieldName: string) => {
  return fields.find((field) => field.name === fieldName);
};
